import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { ChevronIcon } from '../icons';
import { EventIcon } from './event-icon';
import {
  SEVERITY_COLORS,
  SEVERITY_LABEL_KEYS,
  SOURCE_BRANDING,
  formatSource,
  resolveAlertArea,
  tierToLevel,
  timeAgo,
} from './alert-utils';
import type { FeedAlert } from '../../types';

interface SourceGroup {
  readonly sourceApi: string;
  readonly alerts: readonly FeedAlert[];
  readonly highestTier: FeedAlert['severityTier'];
}

function groupBySource(alerts: readonly FeedAlert[]): SourceGroup[] {
  const map = new Map<string, FeedAlert[]>();
  for (const a of alerts) {
    const list = map.get(a.sourceApi);
    if (list) list.push(a);
    else map.set(a.sourceApi, [a]);
  }
  return Array.from(map.entries())
    .map(([sourceApi, items]) => {
      const sorted = [...items].sort((a, b) => a.severityTier - b.severityTier);
      return {
        sourceApi,
        alerts: sorted,
        highestTier: sorted[0]!.severityTier,
      };
    })
    // Sort groups by highest severity, then alphabetically for stable order.
    .sort((x, y) => {
      if (x.highestTier !== y.highestTier) return x.highestTier - y.highestTier;
      return x.sourceApi.localeCompare(y.sourceApi);
    });
}

interface AlertsSectionProps {
  readonly alerts: readonly FeedAlert[];
  readonly onOpenAlert: (alert: FeedAlert) => void;
}

export function AlertsSection({ alerts, onOpenAlert }: AlertsSectionProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupBySource(alerts), [alerts]);
  if (alerts.length === 0) return null;
  return (
    <section className="nw-alerts">
      <div className="nw-alerts__label">{t('alerts.sections.forYourAttention')}</div>
      {groups.map((g) => (
        <SourceAlertGroup
          key={g.sourceApi}
          t={t}
          sourceApi={g.sourceApi}
          alerts={g.alerts}
          onOpenAlert={onOpenAlert}
        />
      ))}
    </section>
  );
}

interface SourceAlertGroupProps {
  readonly t: TFunction;
  readonly sourceApi: string;
  readonly alerts: readonly FeedAlert[];
  readonly onOpenAlert: (alert: FeedAlert) => void;
}

function SourceAlertGroup({ t, sourceApi, alerts, onOpenAlert }: SourceAlertGroupProps) {
  const [expanded, setExpanded] = useState(false);

  if (alerts.length === 0) return null;

  const sourceLabel = formatSource(sourceApi);
  const branding = SOURCE_BRANDING[sourceApi];

  // Highest severity in the group drives the header chip color.
  const headerTier = Math.min(...alerts.map((a) => a.severityTier)) as FeedAlert['severityTier'];
  const headerLevel = tierToLevel(headerTier);
  const headerPalette = SEVERITY_COLORS[headerLevel];
  const headerSeverityLabel = t(SEVERITY_LABEL_KEYS[headerLevel]);

  const countLabel = t('alerts.group.count', { count: alerts.length });

  return (
    <div className="nw-alert-group">
      <button
        type="button"
        className="nw-alert-group__header"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={`${sourceLabel}, ${countLabel}`}
      >
        <span
          className="nw-alert-group__logo"
          style={branding?.tileBg ? { background: branding.tileBg } : undefined}
          aria-hidden
        >
          {branding?.wordmarkUrl ? (
            <img
              src={branding.wordmarkUrl}
              alt=""
              className={`nw-alert-group__logo-img${branding.tintWhite ? ' is-tinted' : ''}`}
            />
          ) : (
            <span className="nw-alert-group__logo-letter">
              {sourceLabel.charAt(0).toUpperCase()}
            </span>
          )}
        </span>
        <span className="nw-alert-group__head-text">
          <span className="nw-alert-group__source">{sourceLabel}</span>
          <span className="nw-alert-group__count">{countLabel}</span>
        </span>
        <span
          className="nw-alert-group__chip"
          style={{
            background: headerPalette.bg,
            borderColor: headerPalette.border,
            color: headerPalette.core,
          }}
        >
          <span
            className="nw-alert-group__chip-dot"
            style={{ background: headerPalette.core }}
            aria-hidden
          />
          {headerSeverityLabel}
        </span>
        <ChevronIcon dir={expanded ? 'down' : 'right'} size={14} stroke={2} />
      </button>

      {expanded && (
        <div className="nw-alert-group__body" role="list">
          {alerts.map((alert) => {
            const level = tierToLevel(alert.severityTier);
            const palette = SEVERITY_COLORS[level];
            const severityLabel = t(SEVERITY_LABEL_KEYS[level]);
            const area = resolveAlertArea(alert);
            return (
              <button
                key={alert.id}
                type="button"
                className="nw-alert-group__row"
                onClick={() => onOpenAlert(alert)}
                aria-label={`${severityLabel}: ${alert.title}`}
                role="listitem"
              >
                <span
                  className="nw-alert-group__row-stripe"
                  style={{ background: palette.core }}
                  aria-hidden
                />
                {/* If the source has a wordmark, render a small version of
                    it on each row (matches mobile). Otherwise fall back
                    to a severity-tinted event-type glyph. */}
                {branding?.wordmarkUrl ? (
                  <span className="nw-alert-group__row-wordmark" aria-hidden>
                    <img
                      src={branding.wordmarkUrl}
                      alt=""
                      className={branding.tintWhite ? 'is-tinted' : ''}
                    />
                  </span>
                ) : (
                  <span className="nw-alert-group__row-icon" style={{ color: palette.core }} aria-hidden>
                    <EventIcon eventType={alert.eventType} size={16} color={palette.core} />
                  </span>
                )}
                <span className="nw-alert-group__row-text">
                  <span className="nw-alert-group__row-title">{alert.title}</span>
                  <span className="nw-alert-group__row-meta">
                    {[area, timeAgo(alert.issuedAt)].filter((s) => s && s.length > 0).join(' · ')}
                  </span>
                </span>
                <ChevronIcon dir="right" size={13} stroke={2} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
